import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    Line,
} from "@vnedyalk0v/react19-simple-maps";
import { ArrowRight, MapPin } from "lucide-react";
import { CORRIDORS, HUB } from "@/data/corridors";

const GEO_URL = "/world-110m.json";

export default function CorridorMap() {
    const [active, setActive] = useState(null);
    const [geoData, setGeoData] = useState(null);
    const navigate = useNavigate();
    const activeC = useMemo(
        () => CORRIDORS.find((c) => c.id === active),
        [active]
    );

    // Load topojson directly (bypasses library's strict HTTPS validator which
    // blocks the bundled `/world-110m.json` when served over plain HTTP in dev).
    useEffect(() => {
        let cancelled = false;
        fetch(GEO_URL)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!cancelled && data) setGeoData(data);
            })
            .catch(() => {
                /* keep map without country fill */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            id="corridors"
            data-testid="corridors-section"
            className="relative bg-[#04110D] text-white py-24 md:py-32 px-4 md:px-8 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid-emerald opacity-40" aria-hidden />
            <div
                aria-hidden
                className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[1000px] rounded-full bg-emerald-500/10 blur-[120px]"
            />

            <div className="relative mx-auto max-w-7xl">
                <div className="max-w-2xl">
                    <span className="text-xs tracking-[0.25em] uppercase font-bold text-emerald-300">
                        Global payment corridors
                    </span>
                    <h2 className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                        Money moves to where your business does.
                    </h2>
                    <p className="mt-4 text-zinc-400">
                        Hover (or tap) a destination to see settlement time, supported currencies
                        and delivery estimate.
                    </p>
                </div>

                <div className="mt-10 rounded-3xl border border-white/10 bg-zinc-950/60 backdrop-blur-xl p-3 sm:p-6">
                    <div className="relative w-full">
                        <ComposableMap
                            projection="geoEqualEarth"
                            projectionConfig={{ scale: 175, center: [25, 20] }}
                            width={980}
                            height={460}
                            style={{ width: "100%", height: "auto" }}
                        >
                            <defs>
                                <linearGradient id="corr-line" x1="0" y1="0" x2="1" y2="0">
                                    <stop offset="0%" stopColor="#34d399" stopOpacity="0.15" />
                                    <stop offset="55%" stopColor="#34d399" stopOpacity="1" />
                                    <stop offset="100%" stopColor="#D4AF37" stopOpacity="1" />
                                </linearGradient>
                                <radialGradient id="pin-glow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#34d399" stopOpacity="0.35" />
                                    <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
                                </radialGradient>
                                <radialGradient id="hub-glow" cx="50%" cy="50%" r="50%">
                                    <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                                </radialGradient>
                            </defs>

                            {geoData && (
                                <Geographies geography={geoData}>
                                    {({ geographies }) =>
                                        geographies.map((geo) => (
                                            <Geography
                                                key={geo.rsmKey}
                                                geography={geo}
                                                style={{
                                                    default: {
                                                        fill: "rgba(16, 185, 129, 0.08)",
                                                        stroke: "rgba(16, 185, 129, 0.28)",
                                                        strokeWidth: 0.5,
                                                        outline: "none",
                                                    },
                                                    hover: {
                                                        fill: "rgba(16, 185, 129, 0.14)",
                                                        stroke: "rgba(16, 185, 129, 0.45)",
                                                        strokeWidth: 0.6,
                                                        outline: "none",
                                                    },
                                                    pressed: {
                                                        fill: "rgba(16, 185, 129, 0.18)",
                                                        outline: "none",
                                                    },
                                                }}
                                            />
                                        ))
                                    }
                                </Geographies>
                            )}

                            {/* Corridor lines */}
                            {CORRIDORS.map((c) => (
                                <Line
                                    key={`line-${c.id}`}
                                    from={HUB.coords}
                                    to={c.coords}
                                    stroke="url(#corr-line)"
                                    strokeWidth={active === c.id ? 2.4 : 1.4}
                                    strokeLinecap="round"
                                    strokeDasharray="5 6"
                                    fill="none"
                                    className="animate-flow-line"
                                    opacity={active && active !== c.id ? 0.35 : 0.95}
                                />
                            ))}

                            {/* Destination markers */}
                            {CORRIDORS.map((c) => {
                                const isActive = active === c.id;
                                return (
                                    <Marker
                                        key={c.id}
                                        coordinates={c.coords}
                                        onMouseEnter={() => setActive(c.id)}
                                        onClick={() => setActive(c.id)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <g data-testid={`corridor-pin-${c.id}`}>
                                            <circle r={16} fill="url(#pin-glow)" className="animate-flow-pulse" />
                                            <circle
                                                r={isActive ? 6.5 : 5}
                                                fill="#34d399"
                                                stroke="#04110D"
                                                strokeWidth={1.5}
                                            />
                                            <text
                                                y={-12}
                                                textAnchor="middle"
                                                style={{
                                                    font: "700 9.5px 'IBM Plex Mono', monospace",
                                                    fill: "#a7f3d0",
                                                    letterSpacing: "0.12em",
                                                    paintOrder: "stroke",
                                                    stroke: "#04110D",
                                                    strokeWidth: 3,
                                                    strokeLinejoin: "round",
                                                }}
                                            >
                                                {c.country.toUpperCase()}
                                            </text>
                                        </g>
                                    </Marker>
                                );
                            })}

                            {/* India hub */}
                            <Marker coordinates={HUB.coords}>
                                <circle r={22} fill="url(#hub-glow)" className="animate-flow-pulse" />
                                <circle r={7.5} fill="#D4AF37" stroke="#ffffff" strokeWidth={1.5} />
                                <text
                                    y={-14}
                                    textAnchor="middle"
                                    style={{
                                        font: "800 10px 'IBM Plex Mono', monospace",
                                        fill: "#fde68a",
                                        letterSpacing: "0.14em",
                                        paintOrder: "stroke",
                                        stroke: "#04110D",
                                        strokeWidth: 3,
                                        strokeLinejoin: "round",
                                    }}
                                >
                                    INDIA · HUB
                                </text>
                            </Marker>
                        </ComposableMap>
                    </div>

                    {/* Tooltip / detail panel */}
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6 min-h-[180px]">
                            {activeC ? (
                                <div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl" aria-hidden>
                                            {activeC.flag}
                                        </span>
                                        <div>
                                            <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">
                                                India ↔ {activeC.country}
                                            </div>
                                            <div className="font-heading text-xl font-bold">
                                                {activeC.country} corridor
                                            </div>
                                        </div>
                                    </div>
                                    <dl className="mt-5 grid grid-cols-3 gap-3 text-sm">
                                        <div>
                                            <dt className="text-[10px] uppercase tracking-widest text-zinc-400">
                                                Settlement
                                            </dt>
                                            <dd className="mt-1 font-mono font-bold">
                                                {activeC.settlement}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-[10px] uppercase tracking-widest text-zinc-400">
                                                Currencies
                                            </dt>
                                            <dd className="mt-1 font-mono font-bold">
                                                {activeC.currencies.join(" · ")}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-[10px] uppercase tracking-widest text-zinc-400">
                                                Delivery
                                            </dt>
                                            <dd className="mt-1 text-zinc-200">
                                                {activeC.delivery}
                                            </dd>
                                        </div>
                                    </dl>
                                    <button
                                        type="button"
                                        data-testid={`corridor-learn-more-${activeC.id}`}
                                        onClick={() => navigate(`/corridors/${activeC.slug}`)}
                                        className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-500 transition-colors"
                                    >
                                        Learn more about {activeC.country}
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex h-full items-center gap-3 text-zinc-400">
                                    <MapPin className="h-5 w-5 text-emerald-400" />
                                    <span>Hover or tap a destination pin to see corridor details.</span>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            {CORRIDORS.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    data-testid={`corridor-chip-${c.id}`}
                                    onMouseEnter={() => setActive(c.id)}
                                    onClick={() => setActive(c.id)}
                                    className={`rounded-2xl border px-4 py-3 text-left transition-all ${
                                        active === c.id
                                            ? "border-emerald-400/60 bg-emerald-500/10"
                                            : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
                                    }`}
                                >
                                    <div className="text-xs uppercase tracking-widest text-zinc-400">
                                        India ↔ {c.country}
                                    </div>
                                    <div className="mt-1 font-mono text-sm font-bold text-white">
                                        {c.settlement} · {c.currencies.join("/")}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
